import { useEffect, useMemo, useState } from 'react';
import { db } from '@/lib/db';
import type { MemberPublic, Suggestion, SuggestionStatus } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  onClose: () => void;
}

const STATUS_OPTIONS: SuggestionStatus[] = [
  'new', 'reviewed', 'in-progress', 'shipped', 'declined', 'duplicate',
];

const STATUS_LABELS: Record<SuggestionStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  'in-progress': 'In progress',
  shipped: 'Shipped',
  declined: 'Declined',
  duplicate: 'Duplicate',
};

const CATEGORY_LABELS: Record<string, string> = {
  idea: 'Idea',
  bug: 'Bug',
  feature: 'Feature',
  praise: 'Praise',
  complaint: 'Complaint',
  other: 'Other',
};

export function AdminModerationSheet({ onClose }: Props) {
  const [items, setItems] = useState<Suggestion[]>([]);
  const [authors, setAuthors] = useState<Record<string, MemberPublic>>({});
  const [filter, setFilter] = useState<SuggestionStatus | 'all'>('new');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await db.suggestions.list();
      setItems(rows);
      const authorIds = [...new Set(rows.map((r) => r.author_id))];
      const fetched = await Promise.all(authorIds.map((id) => db.members.get(id)));
      const map: Record<string, MemberPublic> = {};
      fetched.forEach((m) => { if (m) map[m.id] = m; });
      setAuthors(map);
      setNotesDraft((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (next[r.id] === undefined) next[r.id] = r.admin_notes || '';
        });
        return next;
      });
    } catch (err) {
      setError((err as Error).message || 'Could not load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((s) => s.status === filter);
  }, [items, filter]);

  const save = async (id: string, status: SuggestionStatus) => {
    setSavingId(id);
    setError(null);
    try {
      const updated = await db.suggestions.update(id, {
        status,
        admin_notes: notesDraft[id]?.trim() || null,
      });
      setItems((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } catch (err) {
      setError((err as Error).message || 'Could not save');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Sheet title="Review Feedback" onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        <p className="mb-3 text-xs leading-relaxed text-ink-soft">
          Member suggestions and bugs from the Suggestion Box appear here.
          Update status and admin notes — only visible to admins.
        </p>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {(['all', ...STATUS_OPTIONS] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                filter === s ? 'bg-navy text-cream' : 'border border-line bg-paper text-mute'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-bad/30 bg-bad/5 px-3 py-2 text-xs font-semibold text-bad">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-mute">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-line bg-cream-warm p-6 text-center text-sm text-mute">
            No suggestions in this queue.
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((s) => {
              const author = authors[s.author_id];
              return (
                <div key={s.id} className="rounded-xl border border-line bg-paper p-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gold-deep">
                      {CATEGORY_LABELS[s.category] || s.category}
                    </span>
                    <span className="text-[10px] text-mute">
                      {new Date(s.created_at).toLocaleString([], {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                    </span>
                    {author && (
                      <span className="text-[10px] font-semibold text-navy">
                        {author.name} @{author.handle}
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-ink">&ldquo;{s.body}&rdquo;</p>
                  <textarea
                    value={notesDraft[s.id] ?? ''}
                    onChange={(e) => setNotesDraft((prev) => ({ ...prev, [s.id]: e.target.value }))}
                    placeholder="Admin notes (internal)"
                    rows={2}
                    className="mt-2.5 w-full resize-y rounded-lg border border-line bg-cream px-2.5 py-2 text-xs text-ink outline-none"
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {STATUS_OPTIONS.map((status) => (
                      <button
                        key={status}
                        type="button"
                        disabled={savingId === s.id}
                        onClick={() => void save(s.id, status)}
                        className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                          s.status === status
                            ? 'bg-navy text-cream'
                            : 'border border-line bg-cream-warm text-navy'
                        } disabled:opacity-50`}
                      >
                        {STATUS_LABELS[status]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Sheet>
  );
}
